import { useQuery, useMutation } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

interface AuditLog {
  id: string
  actor_id: string
  actor_email: string
  action: string
  target_type: string
  target_id: string | null
  target_identifier: string | null
  description: string | null
  old_values: Record<string, unknown> | null
  new_values: Record<string, unknown> | null
  ip_address: string | null
  user_agent: string | null
  created_at: string
}

interface ListAuditLogsParams {
  page?: number
  page_size?: number
  action?: string
  actor_id?: string
  target_type?: string
  target_id?: string
  start_date?: string
  end_date?: string
}

interface PaginatedResponse<T> {
  data: T[]
  pagination: {
    page: number
    page_size: number
    total: number
    total_pages: number
  }
}

// List audit logs
export function useAuditLogs(params: ListAuditLogsParams = {}) {
  const { page = 1, page_size = 20, action, actor_id, target_type, target_id, start_date, end_date } = params

  return useQuery({
    queryKey: ['super-admin', 'audit-logs', params],
    queryFn: async (): Promise<PaginatedResponse<AuditLog>> => {
      let query = supabase
        .from('super_admin_audit_logs')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })

      if (action) query = query.eq('action', action)
      if (actor_id) query = query.eq('actor_id', actor_id)
      if (target_type) query = query.eq('target_type', target_type)
      if (target_id) query = query.eq('target_id', target_id)
      if (start_date) query = query.gte('created_at', start_date)
      if (end_date) query = query.lte('created_at', end_date)

      // Pagination
      const from = (page - 1) * page_size
      const to = from + page_size - 1
      query = query.range(from, to)

      const { data, error, count } = await query

      if (error) throw error

      const logs: AuditLog[] = (data || []).map((log: any) => ({
        id: log.id,
        actor_id: log.actor_id,
        actor_email: log.actor_email,
        action: log.action,
        target_type: log.target_type,
        target_id: log.target_id,
        target_identifier: log.metadata?.target_identifier || null,
        description: log.metadata?.description || null,
        old_values: log.old_values,
        new_values: log.new_values,
        ip_address: log.ip_address,
        user_agent: log.user_agent,
        created_at: log.created_at,
      }))

      const total = count || 0
      const total_pages = Math.ceil(total / page_size)

      return {
        data: logs,
        pagination: { page, page_size, total, total_pages },
      }
    },
  })
}

// Get single audit log
export function useAuditLog(id: string) {
  return useQuery({
    queryKey: ['super-admin', 'audit-logs', id],
    queryFn: async (): Promise<AuditLog> => {
      const { data: log, error } = await supabase
        .from('super_admin_audit_logs')
        .select('*')
        .eq('id', id)
        .single()

      if (error) throw error

      return {
        id: log.id,
        actor_id: log.actor_id,
        actor_email: log.actor_email,
        action: log.action,
        target_type: log.target_type,
        target_id: log.target_id,
        target_identifier: log.metadata?.target_identifier || null,
        description: log.metadata?.description || null,
        old_values: log.old_values,
        new_values: log.new_values,
        ip_address: log.ip_address,
        user_agent: log.user_agent,
        created_at: log.created_at,
      }
    },
    enabled: !!id,
  })
}

// Export audit logs
export function useExportAuditLogs() {
  return useMutation({
    mutationFn: async (params: {
      format: 'csv' | 'json'
      start_date?: string
      end_date?: string
      action?: string
      target_type?: string
    }) => {
      let query = supabase
        .from('super_admin_audit_logs')
        .select('*')
        .order('created_at', { ascending: false })

      if (params.action) query = query.eq('action', params.action)
      if (params.target_type) query = query.eq('target_type', params.target_type)
      if (params.start_date) query = query.gte('created_at', params.start_date)
      if (params.end_date) query = query.lte('created_at', params.end_date)

      const { data, error } = await query

      if (error) throw error

      if (params.format === 'json') {
        return new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      } else {
        // CSV format
        const headers = ['id', 'actor_email', 'action', 'target_type', 'target_id', 'created_at']
        const rows = (data || []).map((log: any) =>
          [log.id, log.actor_email, log.action, log.target_type, log.target_id, log.created_at].join(',')
        )
        const csv = [headers.join(','), ...rows].join('\n')
        return new Blob([csv], { type: 'text/csv' })
      }
    },
  })
}
